import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Button, Column, Columns, Separator, Typography } from "@aviary";
import { SvgFullscriptLogo } from "@shared/assets/logo";
import { useBreakpoints } from "@shared/hooks";
import {
  companyLinks,
  productLinks,
  socialLinks,
  supportLinks,
} from "@unauthenticated/catalog/data/CatalogMenuLinks";
import { openCatalogRoutes } from "@unauthenticated/catalog/data/openCatalogRoutes";
import { l } from "@unauthenticated/catalog/locales/i18n";

import { CopyrightStatement } from "./CopyrightStatement";
import { CountrySelector } from "./CountrySelector";
import { FooterBottomLinks } from "./FooterBottomLinks";
import { FooterLinks } from "./FooterLinks";
import { SocialLinks } from "./SocialLinks";

import * as styles from "./Footer.styles";

const Footer: FC = () => {
  const { t } = useTranslation();
  const { phone } = useBreakpoints();

  const renderFooterBottom = () => {
    if (phone.active || phone.lessThan) {
      return (
        <>
          <FooterBottomLinks />
          <Separator css={styles.seperator} />
          <CopyrightStatement />
        </>
      );
    }

    return (
      <>
        <CopyrightStatement />
        <FooterBottomLinks />
      </>
    );
  };

  const renderLinks = () => {
    return (
      <Column columnWidth={6}>
        <Columns>
          <Column columnWidth={4} css={styles.column}>
            <FooterLinks menuHeading={t(l.footer.Product)} links={productLinks} />
          </Column>
          <Column columnWidth={4} css={styles.column}>
            <FooterLinks menuHeading={t(l.footer.Company)} links={companyLinks} />
          </Column>
          <Column columnWidth={4} css={styles.column}>
            <FooterLinks menuHeading={t(l.footer.Support)} links={supportLinks} />
          </Column>
        </Columns>
      </Column>
    );
  };

  return (
    <footer>
      <section css={styles.footerTop}>
        <div css={styles.contentWrapper}>
          <Columns isMultiline>
            <Column columnWidth={6} textAlign="left">
              <Button
                aria-label={t(l.navigation.FullscriptLogo)}
                noStyle
                href={openCatalogRoutes.fullscript}
              >
                <SvgFullscriptLogo css={styles.fsLogo} />
              </Button>
              <div css={styles.footerText}>
                <Typography type="h5" css={styles.whiteText}>
                  {t(l.footer.CarbonNeutral)}
                </Typography>
              </div>
              <SocialLinks links={socialLinks} />
              <CountrySelector />
            </Column>
            {renderLinks()}
          </Columns>
        </div>
      </section>
      <section css={styles.footerBottom}>
        <div css={styles.contentWrapper}>
          <Columns isMultiline>{renderFooterBottom()}</Columns>
        </div>
      </section>
    </footer>
  );
};

export { Footer };
